import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import can from 'ember-can/helpers/can';
import { dropTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import PowerSelect from 'ember-power-select/components/power-select';
import ConditionalLinkTo from 'garaje/components/conditional-link-to';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import doubleConfirmation from 'garaje/helpers/double-confirmation';
import type Location from 'garaje/models/location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface LocaleOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface UpdateWarning {
  header: string;
  message: string;
}

interface I18nConfigSignature {
  Args: {
    /**
     * Array of available locale options for the dropdown
     */
    localeOptions: LocaleOption[];
    /**
     * The location model being configured
     */
    model: Location;
    /**
     * Optional function to transition to a different route
     */
    transitionToRoute?: (route: string) => void;
    /**
     * Function called when locale is updated in the dropdown
     */
    updateLocale: (locale: string) => void;
    /**
     * Optional warning message about app updates required for certain languages
     */
    updateWarning?: UpdateWarning;
  };
}

export default class I18nConfigComponent extends Component<I18nConfigSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @tracked isEditing = false;

  get isDirty(): boolean {
    // model.changedAttributes() is not reactive. Watching model.locale changes triggers a recompute.
    if (!this.args.model.locale) return true;
    return 'locale' in this.args.model.changedAttributes();
  }

  get selectedLocaleOption(): LocaleOption | undefined {
    return this.args.localeOptions.find((option) => option.value === this.args.model.locale);
  }

  get selectedLocaleLabel(): string {
    return this.selectedLocaleOption?.label ?? 'Unknown';
  }

  get isSaveDisabled(): boolean {
    if (!this.isDirty) return true;
    return this.saveTask.isRunning;
  }

  saveTask = dropTask(async (location: Location) => {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_LANGUAGE_SETTINGS_SAVE_BUTTON_CLICKED');
    try {
      await location.save();
      await location.belongsTo('config').reload();

      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.isEditing = false;
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  rollback = (): void => {
    this.args.model.rollbackAttributes();
  };

  edit = (): void => {
    this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_LANGUAGE_SETTINGS_EDIT_BUTTON_CLICKED');
    this.isEditing = true;
  };

  closeEditing = (): void => {
    this.isEditing = false;
  };

  handleLocaleSelection = (option: LocaleOption): void => {
    this.args.updateLocale?.(option.value);
  };

  <template>
    <SettingsPanel
      @isOpen={{this.isEditing}}
      @onCancel={{doubleConfirmation close=this.closeEditing rollback=this.rollback dirty=this.isDirty}}
      data-test-location-i18n
      as |panel|
    >
      <panel.header @iconUri="/assets/images/features/i18n.svg" @title="Language" as |header|>
        <header.description>
          Your default language is
          <strong>
            {{this.selectedLocaleLabel}}
          </strong>
        </header.description>
        <header.controls>
          {{#if this.isEditing}}
            <button
              class="btn btn--small btn--brand"
              disabled={{this.isSaveDisabled}}
              data-test-save-button
              {{on "click" (perform this.saveTask @model)}}
            >
              {{#if this.saveTask.isRunning}}
                Saving...
              {{else}}
                Save
              {{/if}}
            </button>
          {{else}}
            <button data-test-edit-button type="button" {{on "click" this.edit}} class="btn btn--small btn--brand">
              Edit
            </button>
          {{/if}}
        </header.controls>
      </panel.header>
      <panel.body>
        <div class="tokenized-form mt-8 flow-root">
          {{#if @updateWarning}}
            <MessageBox @type="warning" @header={{@updateWarning.header}} class="mb-4" data-test-is-app-updated>
              <p data-test-app-update-required class="mb-0">
                {{@updateWarning.message}}
                <LinkTo @route="visitors.devices.ipads" data-test-app-update-required-link>
                  Manage iPads →
                </LinkTo>
              </p>
            </MessageBox>
          {{/if}}
          <div class="float-left box-border w-6/12" data-test-choose-language-select>
            <label for="_localeSelect">
              Language
            </label>
            <PowerSelect
              @options={{@localeOptions}}
              @selected={{this.selectedLocaleOption}}
              @placeholder="Please select locale"
              @allowClear={{false}}
              @searchEnabled={{false}}
              @onChange={{this.handleLocaleSelection}}
              as |option|
            >
              {{get option "label"}}
              {{#if option.disabled}}
                <span class="float-right">
                  App update required
                </span>
              {{/if}}
            </PowerSelect>
            <p>
              Choose the default language of your Visitors kiosk, and the emails and notifications Envoy sends to hosts
              and invited visitors.
            </p>
            <p>
              To offer more languages to visitors, enable
              <ConditionalLinkTo
                @condition={{can "configure welcome-screen"}}
                @route="visitors.settings.welcome-screen.multiple-languages"
                data-test-multiple-language-link
              >
                multiple languages
              </ConditionalLinkTo>
              .
            </p>
          </div>
        </div>
      </panel.body>
    </SettingsPanel>
  </template>
}
